import type { ReportAggregateFunction } from "./queryReport";

type Value = string | number | boolean | null;

/**
 * Decimal text, as a spreadsheet reads a Number from text: an optional sign,
 * digits with an optional point, an optional exponent, and padding. Hex,
 * binary and octal text (which JavaScript's `Number` reads) is not a Number.
 * The server's SQL reads the same grammar, so both data paths agree.
 */
export const REPORT_NUMBER_TEXT =
  /^\s*[+-]?(\d+\.?\d*|\.\d+)([eE][+-]?\d+)?\s*$/;

/**
 * A value as a Number where it is one — typed (the browser) or text (a
 * Result on the server) — else null, as every Report reads Numbers.
 */
export function reportNumberValue(value: Value | undefined): number | null {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value !== "string" || !REPORT_NUMBER_TEXT.test(value)) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * A column's values reduced to one, in the browser, with Server
 * aggregation's rule: sum, avg, min and max read each value as a Number where
 * it is one (sum is 0 when none is; the others have no value), and count
 * counts the values that aren't Empty.
 */
export function reportAggregate(
  values: Iterable<Value>,
  fn: ReportAggregateFunction,
): number | null {
  const { count, numbers, sum, min, max } = tally(values);
  switch (fn) {
    case "count":
      return count;
    case "sum":
      return sum;
    case "avg":
      return numbers ? sum / numbers : null;
    case "min":
      return numbers ? min : null;
    case "max":
      return numbers ? max : null;
  }
}

/** One pass over a column: its non-Empty count, and the count, sum, min and max of its Numbers. */
function tally(values: Iterable<Value>) {
  let count = 0;
  let numbers = 0;
  let sum = 0;
  let min = Infinity;
  let max = -Infinity;
  for (const value of values) {
    if (value !== null && value !== "") count++;
    const number = reportNumberValue(value);
    if (number === null) continue;
    numbers++;
    sum += number;
    if (number < min) min = number;
    if (number > max) max = number;
  }
  return { count, numbers, sum, min, max };
}
